import * as signalR from '@microsoft/signalr';
import { StorageConstants } from '@/core/constants';
import { LocalStorageService } from '@/services/storage/storage.service';

const TIMEOUT_BETWEEN_RECONNECT = 5000;

const reconnectPolicy: signalR.IRetryPolicy = {
    nextRetryDelayInMilliseconds: retryContext => {
        return TIMEOUT_BETWEEN_RECONNECT;
    }
};

export abstract class SignalRClient {
	public connection: signalR.HubConnection;

	constructor(url: string, logLevel: signalR.LogLevel, autoStart: boolean = true) {
	    this.connection = new signalR.HubConnectionBuilder()
	        .withUrl(url, {
	            accessTokenFactory: () => LocalStorageService.getItem(StorageConstants.Token) || ''
	        })
	        .withAutomaticReconnect(reconnectPolicy)
	        .configureLogging(logLevel)
	        .build();

	    if (autoStart) {
	        this.start();
	    }
	}

	public async reconnect() {
	    await this.stop();
	    await this.start();
	}

	public get connectionId(): string | null {
	    return this.connection.connectionId;
	}

	public async start() {
	    try {
	        await this.connection.start();
	    } catch (e) {
	        console.log(e);
	    }
	}

	public async stop() {
	    try {
	        await this.connection.stop();
	    } catch (e) {
	        console.log(e);
	    }
	}

	public addEvent(event: string, callback: (...args: any[]) => void) {
	    this.connection.on(event, callback);
	}

	public removeEvent(event: string) {
	    this.connection.off(event);
	}

	/*
         Returns a promise that is resolved when the client has sent the invocation to the server,
         or an error occurred. The server may still be handling the invocation when the promise resolves.
     */
	public async sendMessage(methodName: string, ...args: any[]): Promise<void> {
	    return this.connection.send(methodName, args);
	}

	/*
        Returns a promise that is resolved when the server has finished invoking the method (or an error occurred).
        In addition, the Invoke promise can receive a result from the server method, if the server returns a result.
    */
	public async invoke<T>(methodName: string, ...args: any[]): Promise<T> {
	    return this.connection.invoke<T>(methodName, args);
	}
}
